import { Breadcrumbs, Button, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useContext, useState } from "react";
import Dropzone from "react-dropzone";
import toast from "react-hot-toast";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import CustomNode from "./CustomNode";
import { filesData } from "../../../../scripts/context/FileStructure";
import { AppContext } from "../../../../scripts/context/App";

export default function ControlledTreeView() {
  const { user } = useContext(AppContext);
  const { moveFile, currentPath, gettingFiles, project, setState }: any =
    useOutletContext();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dropedFile = async (files: any) => {
    if (!user.permissions.includes("upload_sol_file")) {
      toast.error("You don't have permission to upload files");
      return;
    }

    setLoading(true);

    const formData = new FormData();

    formData.append("project_uuid", project.id);
    files.forEach((file: any, index: number) => {
      if (file.path.split(".")[1] !== "sol") {
        setErrorMessage(
          "be sure to upload a solidity file, not a " + file.path.split(".")[1]
        );

        return;
      }

      formData.append(`sol_files[${index}]`, file);
    });
    formData.append("path", currentPath);

    if (errorMessage) return setLoading(false);
    try {
      await axios.post("/deploy/upload-sol-file", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setState({
        project: {
          ...project,
          files_data: [
            ...project.files_data,
            ...files.map((file: any) => ({
              text: file.name,
              data: {
                fileExtension: file.name.split(".").pop(),
                fileSize: `${file.size} KB`,
                fileType: "sol file",
              },
            })),
          ],
        },
      });
      setLoading(false);
    } catch (error: any) {
      console.log({ error });
      if (error.response.data.errors) {
        toast.error(error.response.data.errors.sol_files[0]);
      } else {
        toast.error(
          error.response?.data?.message ||
            "Error uploading file. Please try again later. or contact support"
        );
      }
      setLoading(false);
    }
  };

  const movePaths = async () => {
    if (!user.permissions.includes("move_file"))
      return toast.error("You don't have permission to move files");

    const { old_path, new_path } = moveFile;
    const newOldPath = new_path
      ? `${new_path}/${old_path.split("/").pop()}`
      : `/${old_path.split("/").pop()}`;

    try {
      await axios.post("/deploy/move-file", {
        project_uuid: project.id,
        old_path: old_path,
        new_path: newOldPath,
      });

      toast.success(
        `${old_path.split("/").pop()} moved to ${
          new_path || "Root"
        } successfully!`
      );
      if (!new_path && !currentPath) {
        setState({
          moveFile: {
            old_path: "",
            new_path: "",
          },
        });
      } else {
        setState({
          project: {
            ...project,
            files_data: project.files_data.filter(
              (file: any) => file.text !== old_path.split("/").pop()
            ),
          },
          moveFile: {
            old_path: "",
            new_path: "",
          },
        });
      }
    } catch (error: any) {
      console.log({ error });

      toast.error(
        error.response.data.message ||
          "Something went wrong. Please try again or contact support"
      );
    }
  };

  const updateCompilationStatus = (fileDetails: filesData) => {
    setState({
      project: {
        ...project,
        files_data: project.files_data.map((file: any) => {
          if (file.text === fileDetails.text) {
            return { ...file, compilation_status: "Compiled" };
          }

          return file;
        }),
      },
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      <Dropzone onDrop={dropedFile} disabled={loading}>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <Box
            {...getRootProps()}
            sx={{
              flex: "1 1 auto",
              border: `2px dashed ${isDragActive ? "#a33c3c" : "#e0e0e0"}`,
              p: 2,
              display: "grid",
              placeContent: "center",
              maxWidth: "500px",
              maxHeight: "150px",
              minHeight: "150px",
            }}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <Typography
                variant="body1"
                align="center"
                sx={{ color: "#242634", fontWeight: "bold" }}
              >
                Drop the files here ...
              </Typography>
            ) : (
              <Typography
                variant="body1"
                align="center"
                sx={{ color: "#242634", fontWeight: "bold" }}
              >
                Click to browse or <br />
                drag and drop your files
              </Typography>
            )}
          </Box>
        )}
      </Dropzone>

      <Box
        sx={{
          border: "1px solid #e0e0e0",
          overflowY: "auto",
          flex: "1 1 auto",
        }}
      >
        {user.permissions.includes("view_files") ? (
          <Box
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Breadcrumbs aria-label="breadcrumb">
              <Box>
                <Button
                  sx={{
                    p: 0,
                    textTransform: "unset",
                    fontSize: "unset",
                    fontWeight: "unset",
                  }}
                  onClick={() => setState({ currentPath: "/" })}
                >
                  root
                </Button>
              </Box>
              {currentPath.split("/").map(
                (path: string, index: number) =>
                  path && (
                    <Box key={index}>
                      {index === currentPath.split("/").length - 1 ? (
                        <Typography
                          variant="body1"
                          sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            width: "160px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {path}
                        </Typography>
                      ) : (
                        <Button
                          sx={{
                            p: 0,
                            textTransform: "unset",
                            fontSize: "unset",
                            fontWeight: "unset",
                            display: "block",

                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            maxWidth: "90px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => {
                            if (gettingFiles) return null;

                            setState({ currentPath: `/${path}` });
                          }}
                        >
                          {path}
                        </Button>
                      )}
                    </Box>
                  )
              )}
            </Breadcrumbs>
            {moveFile.old_path && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "#242634",
                  }}
                >
                  Moving file from {moveFile.old_path.substring(1)} to{" "}
                  {moveFile.new_path?.substring(1) || "Root"}
                </Typography>
                <Button
                  onClick={() => {
                    setState({
                      moveFile: {
                        old_path: "",
                        new_path: "",
                      },
                    });
                  }}
                >
                  Never mind
                </Button>

                <Button
                  onClick={() => {
                    if (!user.permissions.includes("move_file"))
                      return toast.error(
                        "You don't have permission to move files"
                      );

                    movePaths();
                  }}
                >
                  Confirm
                </Button>
              </Box>
            )}
            {project.files_data.length > 0 ? (
              project.files_data.map((file: any, index: number) => (
                <CustomNode
                  key={index}
                  fileDetails={file}
                  allFiles={project.files_data}
                  updateCompilationStatus={updateCompilationStatus}
                  handleClick={() => {
                    if (!file.droppable) return;

                    setState({ currentPath: `${currentPath}/${file.text}` });
                  }}
                />
              ))
            ) : (
              <Typography variant="body1" sx={{ textAlign: "center" }}>
                No files found
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body1" sx={{ textAlign: "center", pt: 3 }}>
            You don't have permission to view files
          </Typography>
        )}
      </Box>
    </Box>
  );
}
